import React, { useState, useEffect, useRef } from 'react';
import HoverPortalDropdown from '../components/HoverPortalDropdown';
import { ClipboardList, Download, Eye, Plus, Settings, FileText, AlertCircle } from 'lucide-react';
import { protocolAPI, protocolsAPI, transcriptionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


interface Protocol {
  protocol_id: string;
  transcription_file_s3_id: string;
  protocol: string;
  tokens_input: number;
  tokens_output: number;
  model_name: string;
  protocol_timestamp: string;
  media_file_s3_id: string;
  protocol_name: string | null;
  message_text: string | null;
  length_seconds: number | null;
  extension: string;
  upload_timestamp: string;
  words_count: number | null;
  speakers_count: number | null;
  title: string;
}

interface Transcription {
  media_file_s3_id: string;
  transcription_file_s3_id: string | null;
  file_name: string | null;
  message_text: string | null;
  length_seconds: number | null;
  extension: string;
  upload_timestamp: string;
  words_count: number | null;
  speakers_count: number | null;
  has_transcription: boolean;
  status: 'completed' | 'processing';
}

interface ProtocolConfig {
  language: string;
  has_speaker_identification: boolean;
  detail_level: string;
  blocks: string[];
  scheme_type?: string;
}

const LOCAL_STORAGE_KEY = 'protocol_generation_in_progress_v1';
const BC_CHANNEL = 'protocol_generation_channel_v1';

export const ProtocolPage: React.FC = () => {
  const { user } = useAuth();
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
  const [isViewingContent, setIsViewingContent] = useState(false);
  const [isCreatingProtocol, setIsCreatingProtocol] = useState(false);
  const [selectedTranscriptionId, setSelectedTranscriptionId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [protocolConfig, setProtocolConfig] = useState<ProtocolConfig>({
    language: 'ru',
    has_speaker_identification: true,
    detail_level: 'средний',
    blocks: ['Участники', 'Описание', 'Заключение', 'Ответственность']
  });

  const bcRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    loadData();
    // восстановим состояние генерации из localStorage (если уже запущено в другой вкладке/странице)
    restoreGenerationState();

    // создаём BroadcastChannel (если поддерживается) для синхронизации между вкладками
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      bcRef.current = new BroadcastChannel(BC_CHANNEL);
      bcRef.current.onmessage = (ev) => handleChannelMessage(ev.data);
    }

    // fallback: слушаем storage-изменения (иначе вкладки не увидят изменения)
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE_KEY) {
        restoreGenerationState();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      if (bcRef.current) {
        bcRef.current.close();
        bcRef.current = null;
      }
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    if (!user?.user_id && !user?.email) {
      setError('Пользователь не авторизован');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const chatId = user.user_id || user.email;

      const [protocolsResponse, transcriptionsResponse] = await Promise.all([
        protocolsAPI.getList(chatId),
        transcriptionsAPI.getList(chatId)
      ]);

      setProtocols(protocolsResponse.protocols || []);
      setTranscriptions(transcriptionsResponse.transcriptions || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке данных:', err);
      setError(err.message || 'Ошибка при загрузке данных');
      toast.error('Ошибка при загрузке данных');
    } finally {
      setIsLoading(false);
    }
  };

  // --- localStorage helpers ---
  const writeCreationFlag = (payload: { transcriptionId: string; startedAt: number }) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // ignore
    }
  };
  const clearCreationFlag = () => {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (e) {}
  };
  const readCreationFlag = (): { transcriptionId: string; startedAt: number } | null => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  const postChannelMessage = (msg: any) => {
    try {
      if (bcRef.current) {
        bcRef.current.postMessage(msg);
      } else {
        // fallback: записываем в localStorage (триггер storage event)
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(msg.__storageMirror || {}));
        // удалим сразу, чтобы не мешать (storage-событие всё равно отработает)
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (e) {
      // ignore
    }
  };

  const handleChannelMessage = (data: any) => {
    // Ожидаем объекты вида { type: 'start' | 'done' | 'error', transcriptionId, message? }
    if (!data || typeof data.type !== 'string') return;
    if (data.type === 'start') {
      setIsCreatingProtocol(true);
      // Если передан transcriptionId — выставим его в селекте, чтобы сохранялось состояние UI
      if (data.transcriptionId) setSelectedTranscriptionId(data.transcriptionId);
    } else if (data.type === 'done') {
      // Убираем спиннер, обновляем список
      clearCreationFlag();
      setIsCreatingProtocol(false);
      setSelectedTranscriptionId('');
      // Обновим список протоколов из сервера
      loadData();
      toast.success('Генерация протокола завершена и добавлена в список.');
    } else if (data.type === 'error') {
      clearCreationFlag();
      setIsCreatingProtocol(false);
      toast.error(data.message || 'Ошибка при генерации протокола');
    }
  };

  const restoreGenerationState = () => {
    const stored = readCreationFlag();
    if (stored) {
      // Если флаг есть и время старта не старше, скажем, 12 часов — считаем процесс запущенным
      const ageMs = Date.now() - (stored.startedAt || 0);
      if (ageMs < 12 * 60 * 60 * 1000) {
        setIsCreatingProtocol(true);
        if (stored.transcriptionId) setSelectedTranscriptionId(stored.transcriptionId);
      } else {
        clearCreationFlag();
        setIsCreatingProtocol(false);
      }
    } else {
      setIsCreatingProtocol(false);
    }
  };

  // --- создание протокола (длинное соединение, 1 вызов API) ---
  const createProtocol = async () => {
    if (!selectedTranscriptionId) {
      toast.error('Выберите стенограмму для создания протокола');
      return;
    }

    // Записываем флаг в localStorage и сообщаем в channel, чтобы другие вкладки/страницы знали о старте
    const startPayload = { transcriptionId: selectedTranscriptionId, startedAt: Date.now() };
    writeCreationFlag(startPayload);
    postChannelMessage({ type: 'start', transcriptionId: selectedTranscriptionId, __storageMirror: startPayload });
    setIsCreatingProtocol(true);

    try {
      // ВАЖНО: здесь предполагается, что protocolAPI.create — это *длинный* запрос,
      // который вернёт управление после завершения генерации (или выбросит ошибку)
      await protocolAPI.create(selectedTranscriptionId, protocolConfig, 'TSU');

      // Успех: очистим флаг, уведомим другие вкладки и обновим список
      clearCreationFlag();
      postChannelMessage({ type: 'done', transcriptionId: selectedTranscriptionId });
      setIsCreatingProtocol(false);
      setSelectedTranscriptionId('');
      await loadData();
      toast.success('Протокол создан успешно!');
    } catch (err: any) {
      console.error('Ошибка при создании протокола:', err);
      clearCreationFlag();
      postChannelMessage({ type: 'error', transcriptionId: selectedTranscriptionId, message: err?.message || 'Ошибка' });
      setIsCreatingProtocol(false);
      toast.error(err?.message || 'Ошибка при создании протокола');
    }
  };

  const downloadProtocol = async (protocol: Protocol, format: 'docx' | 'pdf' = 'docx') => {
    try {
      const blob = await protocolAPI.export(protocol.protocol, 'TSU', format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Протокол_${protocol.title}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Протокол скачан в формате ${format.toUpperCase()}!`);
    } catch (error) {
      toast.error('Ошибка при скачивании протокола');
    }
  };

  const viewProtocol = (protocol: Protocol) => {
    setSelectedProtocol(protocol);
    setIsViewingContent(true);
  };

  const availableTranscriptions = transcriptions.filter(
    (t): t is Transcription & { transcription_file_s3_id: string } =>
      t.has_transcription && typeof t.transcription_file_s3_id === 'string'
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-secondary-600">Загрузка данных...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Протоколы</h1>
          <p className="mt-2 text-secondary-600">
            Создание, просмотр и управление протоколами встреч
          </p>
        </div>
        <div className="card">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="btn-primary"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 relative pb-25">
      {/* Overlay при генерации (перекрывает весь интерфейс) */}
      {isCreatingProtocol && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          aria-live="polite"
        >
          <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-sm"></div>
          <div className="relative bg-white rounded-lg p-6 shadow-lg w-full max-w-md flex flex-col items-center space-y-4">
            <div className="loading-spinner h-10 w-10"></div>
            <div className="text-center">
              <p className="font-medium text-lg text-secondary-900">
                Генерация протокола
              </p>
              <p className="text-sm text-secondary-600 mt-1">
                Генерация протокола занимает около 2 минут, пожалуйста, подождите.
              </p>
              <p className="text-xs text-secondary-500 mt-2">Вы не сможете выбирать другие стенограммы до завершения.</p>
            </div>
          </div>
        </div>
      )}

      {/* Заголовок */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Протоколы</h1>
        <p className="mt-2 text-secondary-600">
          Создание, просмотр и управление протоколами встреч
        </p>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="card">
          <div className="flex items-center">
            <ClipboardList className="h-8 w-8 text-primary-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Всего протоколов</p>
              <p className="text-2xl font-semibold text-secondary-900">{protocols.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Использовано токенов</p>
              <p className="text-2xl font-semibold text-secondary-900">
                {protocols.reduce((sum, p) => sum + (p.tokens_input || 0) + (p.tokens_output || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Settings className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Модель ИИ</p>
              <p className="text-2xl font-semibold text-secondary-900">
                GigaChat 2 Max
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Создание нового протокола */}
      <div className="card">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Создать новый протокол</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Выберите стенограмму
            </label>
            <select
              value={selectedTranscriptionId}
              onChange={(e) => setSelectedTranscriptionId(e.target.value)}
              className="input-field"
              disabled={isCreatingProtocol}
            >
              <option value="">Выберите стенограмму...</option>
              {availableTranscriptions.map((transcription) => (
                <option key={transcription.transcription_file_s3_id} value={transcription.transcription_file_s3_id}>
                  {`${transcription.file_name}.${transcription.extension}` || transcription.message_text}
                  ({format(new Date(transcription.upload_timestamp), 'dd MMM yyyy', { locale: ru })})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={createProtocol}
              disabled={!selectedTranscriptionId || isCreatingProtocol}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingProtocol ? (
                <div className="loading-spinner h-4 w-4"></div>
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>Создать протокол</span>
            </button>
            <button
              onClick={loadData}
              className="btn-secondary"
              disabled={isCreatingProtocol}
            >
              Обновить список
            </button>
          </div>
          {isCreatingProtocol && (
            <p className="text-sm text-secondary-600">
              Генерация протокола занимает около 2 минут. Пожалуйста, дождитесь завершения.
            </p>
          )}
        </div>
      </div>

      {/* Список протоколов */}
      <div className="card z-10">
        <div className="flex items-center justify-between mb-4 z-10">
          <h2 className="text-lg font-semibold text-secondary-900">Список протоколов</h2>
          <button
            onClick={loadData}
            className="btn-secondary text-sm"
            disabled={isCreatingProtocol}
          >
            Обновить
          </button>
        </div>

        {protocols.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardList className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">Нет протоколов</h3>
            <p className="text-secondary-600">
              Создайте протокол из готовой стенограммы
            </p>
          </div>
        ) : (
          <div className="max-h-[30vh] overflow-y-auto space-y-4 pr-2 z-10">
            {protocols.map((protocol) => (
              <div key={protocol.protocol_id} className="border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ClipboardList className="h-5 w-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{protocol.title}</p>
                      <div className="flex items-center space-x-4 text-xs text-secondary-600">
                        <span className="flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          {format(new Date(protocol.protocol_timestamp), 'dd MMM yyyy, HH:mm', { locale: ru })}
                        </span>
                        {protocol.tokens_input && protocol.tokens_output && (
                          <span>{protocol.tokens_input + protocol.tokens_output} токенов</span>
                        )}
                        <span className="capitalize">{protocol.model_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => viewProtocol(protocol)}
                      className="btn-secondary flex items-center space-x-1"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Просмотр</span>
                    </button>
                    <HoverPortalDropdown protocol={protocol} downloadProtocol={downloadProtocol} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно для просмотра протокола */}
      {isViewingContent && selectedProtocol && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <h2 className="text-xl font-semibold text-secondary-900">
                {selectedProtocol.title}
              </h2>
              <button
                onClick={() => setIsViewingContent(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedProtocol.protocol}
                </ReactMarkdown>
              </div>
            </div>
            <div className="flex items-center justify-between p-6 border-t border-secondary-200">
              <div className="text-sm text-secondary-600">
                Создан: {format(new Date(selectedProtocol.protocol_timestamp), 'dd MMMM yyyy, HH:mm', { locale: ru })}
                {selectedProtocol.tokens_input && selectedProtocol.tokens_output && (
                  <span className="ml-4">Токенов: {selectedProtocol.tokens_input + selectedProtocol.tokens_output}</span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => downloadProtocol(selectedProtocol, 'docx')}
                  className="btn-secondary"
                >
                  Скачать DOCX
                </button>
                <button
                  onClick={() => downloadProtocol(selectedProtocol, 'pdf')}
                  className="btn-primary"
                >
                  Скачать PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};